import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from './axiosBasedQuery';

import { HTTP_METHODS, buildQueryString } from '../../utils/handler';
import { TIME_TRACKING_ROUTE } from '../../utils/constant/routeURL';
import { API_BASE_URL } from '../../config';

const { GET, POST } = HTTP_METHODS;
const { GET_TIME_TRACKING } = TIME_TRACKING_ROUTE;

export const PROJECTS_READ_BODY = {
  skip: 0,
  limit: 100,
  select: [
    'name',
    'client',
    'status',
    'isActiveProject',
    'projectKey',
    'projectLead',
    'start',
    'end'
  ],
  filter: { isActiveProject: true },
  populate: {
    name: 'projectLead',
    val: ['name', 'profilePicture']
  }
};

export const timeTrackingApi = createApi({
  reducerPath: 'timeTracking',

  baseQuery: axiosBaseQuery({
    baseUrl: API_BASE_URL,
    credentials: true
  }),

  tagTypes: ['TimeEditRequest', 'TimeLogs'],

  endpoints: (builder) => ({
    getTotalAndTaskLogs: builder.query({
      query: (data) => ({
        url: `${GET_TIME_TRACKING}/get-total-time-and-task-log-group${buildQueryString(data)}`,
        method: GET
      }),
      providesTags: ['TimeLogs']
    }),
    getTotalLogs: builder.query({
      query: (data) => ({
        url: `${GET_TIME_TRACKING}/get-total-time-and-task-log-group${buildQueryString(data)}`,
        method: GET
      }),
      providesTags: ['TimeLogs']
    }),
    getProjects: builder.query({
      query: (body = {}) => ({
        url: '/projects/read',
        method: POST,
        data: { ...PROJECTS_READ_BODY, ...body }
      })
    }),
    getTasksList: builder.query({
      query: ({ projectId, assigneeId, skip = 0, limit = 100 } = {}) => ({
        url: '/tasks/list',
        method: POST,
        data: {
          skip,
          limit,
          filter: {
            ...(projectId ? { projectId } : {}),
            ...(assigneeId ? { assignee: [assigneeId] } : {})
          }
        }
      })
    }),
    updateTimeLog: builder.mutation({
      query: ({ timeLogId, data }) => ({
        url: `${GET_TIME_TRACKING}/update-timelog/${timeLogId}`,
        method: HTTP_METHODS.PUT,
        data: { data }
      })
    }),
    startEndTimer: builder.mutation({
      query: ({ taskId, data }) => ({
        url: `/tasks/start-end-timer/${taskId}`,
        method: HTTP_METHODS.PUT,
        data
      })
    }),
    deleteTimeLog: builder.mutation({
      query: (timeLogId) => ({
        url: `${GET_TIME_TRACKING}/delete-timelog/${timeLogId}`,
        method: HTTP_METHODS.DELETE
      })
    }),
    /** IN locality only — create time edit request (no timeLogId) */
    createTimeEditRequest: builder.mutation({
      query: (body) => ({
        url: `${GET_TIME_TRACKING}/edit-request`,
        method: POST,
        data: body
      }),
      invalidatesTags: ['TimeEditRequest']
    }),
    getTimeEditRequests: builder.query({
      query: (params = {}) => ({
        url: `${GET_TIME_TRACKING}/edit-request${buildQueryString(params)}`,
        method: GET
      }),
      providesTags: ['TimeEditRequest']
    }),
    reviewTimeEditRequest: builder.mutation({
      query: ({ id, status, reviewNote }) => ({
        url: `${GET_TIME_TRACKING}/edit-request/${id}`,
        method: HTTP_METHODS.PUT,
        data: {
          status,
          ...(reviewNote != null && reviewNote !== '' ? { reviewNote } : {})
        }
      }),
      // Status change can create or remove PMS log — refresh list + day logs
      invalidatesTags: ['TimeEditRequest', 'TimeLogs']
    }),
    bulkReviewTimeEditRequests: builder.mutation({
      query: ({ requestIds, status, reviewNote }) => ({
        url: `${GET_TIME_TRACKING}/edit-request/bulk-review`,
        method: HTTP_METHODS.PUT,
        data: {
          requestIds,
          status,
          ...(reviewNote != null && reviewNote !== '' ? { reviewNote } : {})
        }
      }),
      invalidatesTags: ['TimeEditRequest', 'TimeLogs']
    })
  })
});

export const {
  useGetTotalAndTaskLogsQuery,
  useGetTotalLogsQuery,
  useGetProjectsQuery,
  useGetTasksListQuery,
  useUpdateTimeLogMutation,
  useStartEndTimerMutation,
  useDeleteTimeLogMutation,
  useCreateTimeEditRequestMutation,
  useGetTimeEditRequestsQuery,
  useReviewTimeEditRequestMutation,
  useBulkReviewTimeEditRequestsMutation
} = timeTrackingApi;
